import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthState } from "@/hooks/useAuthState";
import { useAdminPlanView } from "@/hooks/useAdminPlanView";

interface HasActivePanelOptions {
  /**
   * Ignora la vista simulada Lite del administrador y devuelve siempre el
   * estado real (facturación, suscripciones): ahí nunca se simula.
   */
  ignoreAdminView?: boolean;
}

/**
 * Returns whether the current user has an active Club Pro subscription
 * (either paid or promo / courtesy — any active subscription on a module
 * with access_type='included' counts). Admins always count as active.
 *
 * Business rule: required to unlock add-on and mentoring purchases.
 *
 * Cuando un administrador activa la vista Lite, el hook devuelve el estado de
 * una cuenta gratuita (hasPanel=false, isAdmin=false) para que todos los muros
 * de la web se comporten igual que para un usuario sin Club Pro.
 */
export function useHasActivePanel(options: HasActivePanelOptions = {}) {
  const { view: adminPlanView } = useAdminPlanView();
  const { isAuthenticated, user, isReady } = useAuthState();
  const [hasPanel, setHasPanel] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!isReady) return;
      if (!isAuthenticated || !user) {
        if (!cancelled) {
          setHasPanel(false);
          setIsAdmin(false);
          setLoading(false);
        }
        return;
      }
      setLoading(true);

      let rolesResult = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);
      if (rolesResult.error) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        if (cancelled) return;
        rolesResult = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", user.id);
      }
      const roles = rolesResult.data;
      const admin = !!roles?.some((r: any) => r.role === "admin");
      if (admin) {
        if (!cancelled) {
          setIsAdmin(true);
          setHasPanel(true);
          setLoading(false);
        }
        return;
      }

      // Nueva lógica de planes (interruptor entitlements_v2): el servidor decide.
      // Con el interruptor apagado se ignora y sigue mandando la lógica antigua.
      const { data: ent } = await (supabase as any).rpc("get_entitlements", { _user_id: user.id });
      if (ent?.v2_enabled === true) {
        if (!cancelled) {
          setHasPanel(!!ent.items?.["roadmap.hoja_de_ruta"]?.enabled);
          setIsAdmin(false);
          setLoading(false);
        }
        return;
      }

      const { data: mc } = await supabase
        .from("module_content")
        .select("module_key, access_type")
        .eq("access_type", "included");
      const includedKeys = new Set((mc ?? []).map((m: any) => m.module_key));
      if (includedKeys.size === 0) {
        if (!cancelled) {
          setHasPanel(false);
          setLoading(false);
        }
        return;
      }

      const { data: subs } = await supabase
        .from("user_module_subscriptions")
        .select("module_key, status")
        .eq("user_id", user.id)
        .eq("status", "active");
      let has = !!(subs ?? []).some((s: any) => includedKeys.has(s.module_key));

      // Manual / courtesy Panel access (granted from the back office) also counts:
      // these users must pay only the proportional mentoring price.
      if (!has) {
        const { data: manual } = await supabase
          .from("client_panel_access")
          .select("user_id")
          .eq("user_id", user.id)
          .maybeSingle();
        has = !!manual;
      }

      if (!cancelled) {
        setHasPanel(has);
        setIsAdmin(false);
        setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [isReady, isAuthenticated, user]);

  const simulateLite = !options.ignoreAdminView && isAdmin && adminPlanView === "lite";
  if (simulateLite) return { hasPanel: false, isAdmin: false, loading };

  return { hasPanel, isAdmin, loading };
}
